import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { mergeRegister } from '@lexical/utils';
import {
  $getNodeByKey,
  $getSelection,
  $isRangeSelection,
  BLUR_COMMAND,
  COMMAND_PRIORITY_CRITICAL,
  KEY_ARROW_DOWN_COMMAND,
  KEY_ARROW_UP_COMMAND,
  KEY_ENTER_COMMAND,
  KEY_ESCAPE_COMMAND,
  NodeKey,
} from 'lexical';
import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { $isPlaceholderNode } from '../nodes/PlaceholderNode';
import { NEXT_PLACEHOLDER_COMMAND } from './TemplatePlugin';

const MAX_SUGGESTIONS = 8;

interface SuggestionMenu {
  nodeKey: NodeKey;
  items: string[];
  index: number;
  top: number;
  left: number;
}

export interface PlaceholderSuggestionsPluginProps {
  /** Suggestions per placeholder name, e.g. `{ Person: ['Müller, Hans'] }`. */
  options: Record<string, string[]>;
}

export function PlaceholderSuggestionsPlugin({
  options,
}: PlaceholderSuggestionsPluginProps) {
  const [editor] = useLexicalComposerContext();
  const [menu, setMenu] = useState<SuggestionMenu | null>(null);
  const menuRef = useRef<SuggestionMenu | null>(null);
  const dismissedKeyRef = useRef<NodeKey | null>(null);

  const showMenu = useCallback((next: SuggestionMenu | null) => {
    menuRef.current = next;
    setMenu(next);
  }, []);

  const pick = useCallback(
    (nodeKey: NodeKey, value: string) => {
      const node = $getNodeByKey(nodeKey);
      if ($isPlaceholderNode(node)) {
        node.setTextContent(value);
        node.select(value.length, value.length);
      }
      showMenu(null);
      editor.dispatchCommand(NEXT_PLACEHOLDER_COMMAND, undefined);
    },
    [editor, showMenu],
  );

  useEffect(() => {
    const move = (step: 1 | -1, event: KeyboardEvent | null) => {
      const current = menuRef.current;
      if (!current) {
        return false;
      }
      event?.preventDefault();
      const count = current.items.length;
      showMenu({ ...current, index: (current.index + step + count) % count });
      return true;
    };

    return mergeRegister(
      editor.registerUpdateListener(({ editorState }) => {
        const next = editorState.read(() => {
          const selection = $getSelection();
          if (!$isRangeSelection(selection)) {
            return null;
          }
          const node = selection.anchor.getNode();
          if (
            !$isPlaceholderNode(node) ||
            !node.is(selection.focus.getNode())
          ) {
            return null;
          }
          const all = options[node.getPlaceholderName()];
          if (!all?.length) {
            return null;
          }
          const query = node.isFilled()
            ? node.getTextContent().toLowerCase()
            : '';
          const items = all
            .filter((option) => option.toLowerCase().includes(query))
            .slice(0, MAX_SUGGESTIONS);
          return items.length > 0 ? { nodeKey: node.getKey(), items } : null;
        });

        // Esc closes the list for this field until the caret moves elsewhere.
        const dismissed = dismissedKeyRef.current;
        if (next?.nodeKey !== dismissed) {
          dismissedKeyRef.current = null;
        }
        const rect =
          next &&
          next.nodeKey !== dismissed &&
          editor.getElementByKey(next.nodeKey)?.getBoundingClientRect();
        if (!next || !rect) {
          if (menuRef.current) {
            showMenu(null);
          }
          return;
        }
        const previous = menuRef.current;
        const keepIndex =
          previous?.nodeKey === next.nodeKey &&
          previous.items.join('\n') === next.items.join('\n');
        const top = rect.bottom + 2;
        if (keepIndex && previous.top === top && previous.left === rect.left) {
          return;
        }
        showMenu({
          ...next,
          index: keepIndex ? previous.index : 0,
          top,
          left: rect.left,
        });
      }),
      editor.registerCommand(
        KEY_ARROW_DOWN_COMMAND,
        (event) => move(1, event),
        COMMAND_PRIORITY_CRITICAL,
      ),
      editor.registerCommand(
        KEY_ARROW_UP_COMMAND,
        (event) => move(-1, event),
        COMMAND_PRIORITY_CRITICAL,
      ),
      editor.registerCommand(
        KEY_ENTER_COMMAND,
        (event) => {
          const current = menuRef.current;
          if (!current || event?.shiftKey) {
            return false;
          }
          event?.preventDefault();
          pick(current.nodeKey, current.items[current.index]);
          return true;
        },
        COMMAND_PRIORITY_CRITICAL,
      ),
      // Both only close the list; Esc still reaches the template plugin.
      editor.registerCommand(
        KEY_ESCAPE_COMMAND,
        () => {
          dismissedKeyRef.current = menuRef.current?.nodeKey ?? null;
          showMenu(null);
          return false;
        },
        COMMAND_PRIORITY_CRITICAL,
      ),
      editor.registerCommand(
        BLUR_COMMAND,
        () => {
          showMenu(null);
          return false;
        },
        COMMAND_PRIORITY_CRITICAL,
      ),
    );
  }, [editor, options, pick, showMenu]);

  if (!menu) {
    return null;
  }

  return createPortal(
    <ul
      className="lexical-placeholder-suggestions"
      role="listbox"
      style={{ top: menu.top, left: menu.left }}
    >
      {menu.items.map((item, index) => (
        <li
          key={item}
          role="option"
          aria-selected={index === menu.index}
          className={index === menu.index ? 'active' : undefined}
          // Keep the caret in the editor instead of moving focus to the list.
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => editor.update(() => pick(menu.nodeKey, item))}
        >
          {item}
        </li>
      ))}
    </ul>,
    document.body,
  );
}
